import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AdminHeader from "../components/AdminHeader";
import type {
  CampaignSummary,
  CampaignUserFilterKey,
  CampaignUserFilters,
  EmailCampaignState,
  ScheduledEmail,
} from "./types";
import type { DuplicateCampaignPayload } from "./components/DuplicateCampaignModal";
import { SettingsSection } from "./components/SettingsSection";
import { BatchTestingSection } from "./components/BatchTestingSection";
import { BatchComparisonSection } from "./components/BatchComparisonSection";
import { UserHistorySection } from "./components/SentEmailHistorySection";
import { ScheduledEmailsSection } from "./components/ScheduledQueueSection";
import { AnalyticsSection } from "./components/AnalyticsSection";
import {
  CharacterLibraryProvider,
  useCharacterLibrary,
  useCampaigns,
} from "./CharacterLibraryProvider";
import {
  cancelScheduledEmail,
  fetchCampaignMetrics,
  fetchCampaignUsers,
  fetchScheduledEmails,
  type CampaignMetric,
  type CampaignUserApi,
} from "../../../api/campaigns";
import { mapCampaignUsersFromApi } from "./helpers/userMappers";
import { mapScheduledEmailsFromApi } from "./helpers/scheduledEmailMappers";

type TabKey =
  | "settings"
  | "batch"
  | "comparisons"
  | "history"
  | "scheduled"
  | "analytics";

const TABS: Array<{ key: TabKey; label: string; badge?: string }> = [
  { key: "settings", label: "Campaign Settings" },
  { key: "batch", label: "Email Management & Analytics" },
  { key: "comparisons", label: "Batch Comparisons" },
  { key: "history", label: "Email History" },
  { key: "scheduled", label: "Scheduled Queue" },
];

const EmailCampaignModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("settings");
  const [initialCampaignName, setInitialCampaignName] = useState("");
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [isSavingCharacterSelection, setIsSavingCharacterSelection] =
    useState(false);
  const [isSavingReplyTiming, setIsSavingReplyTiming] = useState(false);
  const [isSavingNoChat, setIsSavingNoChat] = useState(false);
  const [isSavingReachOut, setIsSavingReachOut] = useState(false);
  const [isDuplicatingCampaign, setIsDuplicatingCampaign] = useState(false);
  const { characters: sharedCharacters } = useCharacterLibrary();
  const [remoteUsers, setRemoteUsers] = useState<CampaignUserApi[] | null>(
    null
  );
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmail[]>([]);
  const [isLoadingScheduledEmails, setIsLoadingScheduledEmails] =
    useState(false);
  const [scheduledFilters, setScheduledFilters] = useState({
    status: "pending",
    jobType: "all",
  });
  const [scheduledCampaignFilter, setScheduledCampaignFilter] =
    useState<string>("all");
  const [userFilters, setUserFilters] = useState<CampaignUserFilters>({
    sort: "latest",
    paidUsersOnly: false,
    ranOutOfTokensOnly: false,
    minTokens: null,
    maxTokens: null,
    fromDate: null,
    toDate: null,
    maxConversationCount: null,
    minUniqueCharacters: null,
    requiredCharacterIds: [],
  });
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetric[]>([]);
  const [isLoadingCampaignMetrics, setIsLoadingCampaignMetrics] =
    useState(false);
  const {
    campaigns,
    activeCampaignId: liveActiveCampaignId,
    selectedCampaignId,
    selectCampaign,
    createCampaign,
    duplicateCampaign,
    updateCampaignData,
    saveReengagementSequence,
    saveReengagementCharacterSelection,
    saveCharacterReplyTiming,
    saveNoChatConfiguration,
    saveReachOutRules,
    activateCampaign,
    deactivateCampaign,
    deleteCampaign,
  } = useCampaigns();

  const resolvedSelectedCampaignId =
    selectedCampaignId ?? campaigns[0]?.id ?? "";

  const characterLibrary = sharedCharacters;

  const selectedCampaign = useMemo(
    () =>
      campaigns.find((campaign) => campaign.id === resolvedSelectedCampaignId),
    [campaigns, resolvedSelectedCampaignId]
  );

  const campaignSummaries: CampaignSummary[] = useMemo(
    () => campaigns.map(({ id, name, status }) => ({ id, name, status })),
    [campaigns]
  );

  const selectedTabLabel = useMemo(
    () => TABS.find((tab) => tab.key === activeTab)?.label ?? "",
    [activeTab]
  );

  const currentCampaignLabel = useMemo(() => {
    if (!selectedCampaign) return "";
    const statusSuffix = selectedCampaign.status === "active" ? " (Live)" : "";
    return `${selectedCampaign.name}${statusSuffix}`;
  }, [selectedCampaign]);

  const updateSelectedCampaign = (
    updater: (current: EmailCampaignState) => EmailCampaignState
  ) => {
    if (!selectedCampaign) return;
    updateCampaignData(selectedCampaign.id, updater);
  };

  const {
    sort,
    paidUsersOnly,
    ranOutOfTokensOnly,
    minTokens,
    maxTokens,
    fromDate,
    toDate,
    maxConversationCount,
    minUniqueCharacters,
    requiredCharacterIds,
  } = userFilters;

  useEffect(() => {
    let isMounted = true;

    const loadCampaignUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const apiUsers = await fetchCampaignUsers({
          sort,
          // Keep fetch broad; paid filtering is applied client-side via isPaidUser().
          // This avoids over-narrowing to only active payers.
          // paidUsersOnly: paidUsersOnly ? true : undefined,
          minTokens: typeof minTokens === "number" ? minTokens : undefined,
          maxTokens: typeof maxTokens === "number" ? maxTokens : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          characterIds: requiredCharacterIds,
          minUniqueCharacters: minUniqueCharacters ?? undefined,
          maxConversationCount: maxConversationCount ?? undefined,
        });
        if (!isMounted) return;
        setRemoteUsers(apiUsers);
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to load campaign users", error);
        toast.error("Failed to load campaign users.");
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      }
    };

    void loadCampaignUsers();

    return () => {
      isMounted = false;
    };
  }, [
    sort,
    paidUsersOnly,
    ranOutOfTokensOnly,
    minTokens,
    maxTokens,
    fromDate,
    toDate,
    maxConversationCount,
    minUniqueCharacters,
    requiredCharacterIds,
  ]);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingCampaignMetrics(true);

    fetchCampaignMetrics()
      .then((metrics) => {
        if (!isMounted) return;
        setCampaignMetrics(metrics);
      })
      .catch((error) => {
        console.error("Failed to load campaign metrics", error);
        toast.error("Failed to load campaign metrics.");
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingCampaignMetrics(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectCampaign = (campaignId: string) => {
    if (!campaignId || campaignId === resolvedSelectedCampaignId) return;
    selectCampaign(campaignId);
  };

  const createCampaignWithFeedback = async (name: string) => {
    const safeName = name.trim() || "Untitled Campaign";
    try {
      await createCampaign(safeName);
      return true;
    } catch (error) {
      console.error("Failed to create campaign", error);
      toast.error("Failed to create campaign. Please try again.");
      return false;
    }
  };

  const handleCreateCampaign = (name: string) => {
    void createCampaignWithFeedback(name);
  };

  const handleDuplicateCampaign = async (
    payload: DuplicateCampaignPayload
  ) => {
    const safeName =
      payload.name.trim() || "Untitled campaign copy";
    setIsDuplicatingCampaign(true);
    try {
      const newCampaign = await duplicateCampaign(
        payload.sourceCampaignId,
        {
          name: safeName,
          sections: payload.sections,
        }
      );
      toast.success("Campaign duplicated. Review settings before activating.");
      selectCampaign(newCampaign.id);
    } catch (error) {
      console.error("Failed to duplicate campaign", error);
      toast.error("Failed to duplicate campaign. Please try again.");
    } finally {
      setIsDuplicatingCampaign(false);
    }
  };

  const selectedState = selectedCampaign?.data;

  const handleUserSortChange = (sort: CampaignUserFilters["sort"]) => {
    setUserFilters((current) =>
      current.sort === sort ? current : { ...current, sort }
    );
  };

  const handleUserFilterToggle = (key: CampaignUserFilterKey) => {
    setUserFilters((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleUserAdvancedFiltersApply = (filters: {
    minTokens: number | null;
    maxTokens: number | null;
    fromDate: string | null;
    toDate: string | null;
    maxConversationCount: number | null;
    minUniqueCharacters: number | null;
    requiredCharacterIds: string[];
  }) => {
    setUserFilters((current) => ({
      ...current,
      minTokens: filters.minTokens,
      maxTokens: filters.maxTokens,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      maxConversationCount: filters.maxConversationCount,
      minUniqueCharacters: filters.minUniqueCharacters,
      requiredCharacterIds: filters.requiredCharacterIds,
    }));
  };

  const refreshScheduledEmails = useCallback(async () => {
    const targetCampaignIds =
      scheduledCampaignFilter === "all"
        ? campaigns.map((campaign) => campaign.id).filter(Boolean)
        : [scheduledCampaignFilter];

    if (!targetCampaignIds.length) {
      setScheduledEmails([]);
      return;
    }

    setIsLoadingScheduledEmails(true);
    const failedCampaigns: string[] = [];

    try {
      const results = await Promise.all(
        targetCampaignIds.map(async (campaignId) => {
          if (!campaignId) return [];
          const campaignSummary = campaigns.find(
            (campaign) => campaign.id === campaignId
          );
          try {
            const response = await fetchScheduledEmails(campaignId, {
              status:
                scheduledFilters.status === "all"
                  ? "all"
                  : scheduledFilters.status,
              jobType:
                scheduledFilters.jobType === "all"
                  ? undefined
                  : scheduledFilters.jobType,
            });
            return mapScheduledEmailsFromApi(response.results, {
              campaignId,
              campaignName: campaignSummary?.name ?? "Unknown campaign",
            });
          } catch (error) {
            console.error(
              `Failed to load scheduled emails for campaign ${campaignId}`,
              error
            );
            failedCampaigns.push(campaignSummary?.name ?? campaignId);
            return [];
          }
        })
      );

      const flattened = results.flat();
      flattened.sort(
        (a, b) =>
          new Date(a.scheduledFor).getTime() -
          new Date(b.scheduledFor).getTime()
      );
      setScheduledEmails(flattened);

      if (failedCampaigns.length) {
        toast.error(
          `Failed to load scheduled emails for ${failedCampaigns.join(", ")}.`
        );
      }
    } finally {
      setIsLoadingScheduledEmails(false);
    }
  }, [campaigns, scheduledCampaignFilter, scheduledFilters]);

  const handleCancelScheduledEmail = useCallback(
    async (scheduledEmail: ScheduledEmail) => {
      const fallbackCampaignId =
        scheduledCampaignFilter !== "all"
          ? scheduledCampaignFilter
          : resolvedSelectedCampaignId;
      const campaignId = scheduledEmail.campaignId ?? fallbackCampaignId;
      if (!campaignId) {
        toast.error("Unable to determine campaign for this scheduled email.");
        return;
      }
      try {
        await cancelScheduledEmail(campaignId, scheduledEmail.id);
        toast.success("Scheduled email cancelled.");
        await refreshScheduledEmails();
      } catch (error) {
        console.error("Failed to cancel scheduled email", error);
        toast.error("Failed to cancel scheduled email. Please try again.");
        throw error;
      }
    },
    [
      refreshScheduledEmails,
      resolvedSelectedCampaignId,
      scheduledCampaignFilter,
    ]
  );

  useEffect(() => {
    if (activeTab !== "scheduled") return;
    void refreshScheduledEmails();
  }, [activeTab, refreshScheduledEmails]);

  const handleScheduledStatusChange = (value: string) => {
    setScheduledFilters((current) => ({ ...current, status: value }));
  };

  const handleScheduledJobTypeChange = (value: string) => {
    setScheduledFilters((current) => ({ ...current, jobType: value }));
  };

  const handleScheduledCampaignChange = (campaignId: string) => {
    setScheduledCampaignFilter(campaignId || "all");
  };

  useEffect(() => {
    if (
      scheduledCampaignFilter !== "all" &&
      !campaigns.some((campaign) => campaign.id === scheduledCampaignFilter)
    ) {
      setScheduledCampaignFilter("all");
    }
  }, [campaigns, scheduledCampaignFilter]);

  const remoteMappedUsers = useMemo(() => {
    if (!remoteUsers) return [];
    return mapCampaignUsersFromApi(remoteUsers, characterLibrary);
  }, [remoteUsers, characterLibrary]);

  const isUsingRemoteUsers = remoteMappedUsers.length > 0;

  const usersForBatchTesting = isUsingRemoteUsers
    ? remoteMappedUsers
    : selectedState?.users ?? [];

  const handleActivateCampaign = (campaignId: string) =>
    activateCampaign(campaignId)
      .then(() => {
        toast.success("Campaign activated.");
      })
      .catch((error) => {
        console.error("Failed to activate campaign", error);
        toast.error("Failed to activate campaign. Please try again.");
      });

  const handleDeactivateCampaign = (campaignId: string) =>
    deactivateCampaign(campaignId)
      .then(() => {
        toast.success("Campaign deactivated.");
      })
      .catch((error) => {
        console.error("Failed to deactivate campaign", error);
        toast.error("Failed to deactivate campaign. Please try again.");
      });

  const handleDeleteCampaign = (campaignId: string) =>
    deleteCampaign(campaignId)
      .then(() => {
        toast.success("Campaign deleted.");
      })
      .catch((error) => {
        console.error("Failed to delete campaign", error);
        toast.error("Failed to delete campaign. Please try again.");
      });

  const handleSaveSequence = () => {
    if (!selectedCampaign || !selectedState) return;
    setIsSavingSequence(true);
    saveReengagementSequence(
      selectedCampaign.id,
      selectedState.settings.sequence
    )
      .then(() => {
        toast.success("Reengagement sequence saved.");
      })
      .catch((error) => {
        console.error("Failed to save sequence", error);
        toast.error("Failed to save sequence. Please try again.");
      })
      .finally(() => {
        setIsSavingSequence(false);
      });
  };

  const handleSaveCharacterSelection = () => {
    if (!selectedCampaign || !selectedState) return;
    setIsSavingCharacterSelection(true);
    saveReengagementCharacterSelection(
      selectedCampaign.id,
      selectedState.settings
    )
      .then(() => {
        toast.success("Character selection saved.");
      })
      .catch((error) => {
        console.error("Failed to save character selection", error);
        toast.error("Failed to save character selection. Please try again.");
      })
      .finally(() => {
        setIsSavingCharacterSelection(false);
      });
  };

  const handleSaveReplyTiming = () => {
    if (!selectedCampaign || !selectedState) return;
    setIsSavingReplyTiming(true);
    saveCharacterReplyTiming(
      selectedCampaign.id,
      selectedState.settings.threadFollowUp
    )
      .then(() => {
        toast.success("Reply timing saved.");
      })
      .catch((error) => {
        console.error("Failed to save reply timing", error);
        toast.error("Failed to save reply timing. Please try again.");
      })
      .finally(() => {
        setIsSavingReplyTiming(false);
      });
  };

  const handleSaveNoChatConfiguration = () => {
    if (!selectedCampaign || !selectedState) return;
    setIsSavingNoChat(true);
    saveNoChatConfiguration(
      selectedCampaign.id,
      selectedState.promptLibrary.noChat
    )
      .then(() => {
        toast.success("No-chat introduction saved.");
      })
      .catch((error) => {
        console.error("Failed to save no-chat intro", error);
        toast.error("Failed to save no-chat intro. Please try again.");
      })
      .finally(() => {
        setIsSavingNoChat(false);
      });
  };

  const handleSaveReachOutRules = () => {
    if (!selectedCampaign || !selectedState) return;
    const invalidRule = selectedState.premiumCriteria.characterRules.find(
      (rule) => {
        const hasSpecificCharacters =
          Array.isArray(rule.triggerCharacterIds) &&
          rule.triggerCharacterIds.length > 0;
        const hasCountRule =
          typeof rule.triggerAfterAnyCount === "number" &&
          rule.triggerAfterAnyCount > 0;
        return !hasSpecificCharacters && !hasCountRule;
      }
    );
    if (invalidRule) {
      toast.error(
        "Each reach-out rule needs specific characters or a minimum count."
      );
      return;
    }
    setIsSavingReachOut(true);
    saveReachOutRules(
      selectedCampaign.id,
      selectedState.premiumCriteria.characterRules
    )
      .then(() => {
        toast.success("Reach-out rules saved.");
      })
      .catch((error) => {
        console.error("Failed to save reach-out rules", error);
        toast.error("Failed to save reach-out rules. Please try again.");
      })
      .finally(() => {
        setIsSavingReachOut(false);
      });
  };

  const renderActiveSection = () => {
    if (!selectedState || !selectedCampaign) {
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500 space-y-4">
          <div>
            No campaigns available. Create a new campaign to get started.
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center">
            <input
              type="text"
              value={initialCampaignName}
              onChange={(event) => setInitialCampaignName(event.target.value)}
              placeholder="Campaign name"
              className="w-full max-w-xs rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            />
            <button
              type="button"
              onClick={() =>
                createCampaignWithFeedback(initialCampaignName).then(
                  (success) => {
                    if (success) {
                      setInitialCampaignName("");
                    }
                  }
                )
              }
              className="rounded-xl bg-[var(--primary-color)] px-4 py-2.5 text-sm font-semibold text-white"
            >
              Create Campaign
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case "settings":
        return (
          <SettingsSection
            settings={selectedState.settings}
            sendTimeOptions={selectedState.promptLibrary.sendTimeOptions}
            premiumCriteria={selectedState.premiumCriteria}
            noChatSchedule={selectedState.promptLibrary.noChat.schedule}
            noChatTemplate={selectedState.promptLibrary.noChat}
            premiumPrompt={selectedState.promptLibrary.premium}
            characters={characterLibrary}
            campaigns={campaignSummaries}
            selectedCampaignId={selectedCampaign.id}
            liveActiveCampaignId={liveActiveCampaignId}
            onSelectCampaign={handleSelectCampaign}
            onCreateCampaign={handleCreateCampaign}
            onDuplicateCampaign={handleDuplicateCampaign}
            isDuplicatingCampaign={isDuplicatingCampaign}
            onActivateCampaign={() =>
              handleActivateCampaign(selectedCampaign.id)
            }
            onDeactivateCampaign={() =>
              handleDeactivateCampaign(selectedCampaign.id)
            }
            onDeleteCampaign={() => handleDeleteCampaign(selectedCampaign.id)}
            onSaveSequence={handleSaveSequence}
            isSavingSequence={isSavingSequence}
            onSaveCharacterSelection={handleSaveCharacterSelection}
            isSavingCharacterSelection={isSavingCharacterSelection}
            onSaveCharacterReplyTiming={handleSaveReplyTiming}
            isSavingCharacterReplyTiming={isSavingReplyTiming}
            onSaveNoChatConfiguration={handleSaveNoChatConfiguration}
            isSavingNoChatConfiguration={isSavingNoChat}
            onSaveReachOutRules={handleSaveReachOutRules}
            isSavingReachOutRules={isSavingReachOut}
            onChange={(settings) =>
              updateSelectedCampaign((current) => ({ ...current, settings }))
            }
            onUpdatePremium={(criteria) =>
              updateSelectedCampaign((current) => ({
                ...current,
                premiumCriteria: criteria,
              }))
            }
            onUpdateNoChatSchedule={(schedule) =>
              updateSelectedCampaign((current) => ({
                ...current,
                promptLibrary: {
                  ...current.promptLibrary,
                  noChat: {
                    ...current.promptLibrary.noChat,
                    schedule,
                    lastUpdated: new Date().toISOString(),
                  },
                },
              }))
            }
            onUpdateNoChatTemplate={(template) =>
              updateSelectedCampaign((current) => ({
                ...current,
                promptLibrary: { ...current.promptLibrary, noChat: template },
              }))
            }
            onUpdatePremiumPrompt={(prompt) =>
              updateSelectedCampaign((current) => ({
                ...current,
                promptLibrary: { ...current.promptLibrary, premium: prompt },
              }))
            }
          />
        );
      case "batch":
        return (
          <BatchTestingSection
            users={usersForBatchTesting}
            batchConfigurations={selectedState.batchConfigurations}
            sendTimeOptions={selectedState.promptLibrary.sendTimeOptions}
            isLoadingUsers={isLoadingUsers}
            isUsingRemoteUsers={isUsingRemoteUsers}
            sortOption={userFilters.sort}
            onChangeSort={handleUserSortChange}
            filterToggles={{
              paidUsersOnly: userFilters.paidUsersOnly,
              ranOutOfTokensOnly: userFilters.ranOutOfTokensOnly,
            }}
            onToggleFilter={handleUserFilterToggle}
            currentCampaignName={currentCampaignLabel}
            campaignOptions={campaignSummaries}
            selectedCampaignId={resolvedSelectedCampaignId}
            onSelectCampaign={handleSelectCampaign}
            minTokensFilter={userFilters.minTokens ?? null}
            maxTokensFilter={userFilters.maxTokens ?? null}
            fromDateFilter={userFilters.fromDate ?? null}
            toDateFilter={userFilters.toDate ?? null}
            maxConversationCountFilter={
              userFilters.maxConversationCount ?? null
            }
            minUniqueCharactersFilter={userFilters.minUniqueCharacters ?? null}
            requiredCharacterFilters={userFilters.requiredCharacterIds ?? []}
            availableCharacters={characterLibrary}
            onApplyAdvancedFilters={handleUserAdvancedFiltersApply}
          />
        );
      case "comparisons":
        return (
          <BatchComparisonSection
            campaignMetrics={campaignMetrics}
            isLoading={isLoadingCampaignMetrics}
          />
        );
      case "history":
        return <UserHistorySection campaignOptions={campaignSummaries} />;
      case "scheduled":
        return (
          <ScheduledEmailsSection
            emails={scheduledEmails}
            isLoading={isLoadingScheduledEmails}
            statusFilter={scheduledFilters.status}
            jobTypeFilter={scheduledFilters.jobType}
            campaignFilter={scheduledCampaignFilter}
            campaignOptions={campaignSummaries}
            onChangeStatus={handleScheduledStatusChange}
            onChangeJobType={handleScheduledJobTypeChange}
            onChangeCampaign={handleScheduledCampaignChange}
            onRefresh={refreshScheduledEmails}
            onCancel={handleCancelScheduledEmail}
          />
        );
      case "analytics":
        return (
          <AnalyticsSection
            overview={selectedState.analytics.overview}
            sequencePerformance={selectedState.analytics.sequencePerformance}
            characterPerformance={selectedState.analytics.characterPerformance}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin">
      <AdminHeader />
      <div className="px-4 sm:px-6 lg:px-10 pb-16">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="title text-3xl font-semibold text-gray-900">
              Email Campaigns
            </div>
            <div className="sub-title mt-2 text-gray-500">
              Configure, test, and monitor automated character follow-ups.
            </div>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 shadow-sm">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                  tab.key === activeTab
                    ? "bg-[var(--primary-color)] text-white shadow"
                    : "text-gray-600 hover:bg-[var(--primary-color)]/10"
                }`}
              >
                {tab.label}
                {tab.badge ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/80 px-2 text-xs font-semibold text-[var(--primary-color)]">
                    {tab.badge}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {selectedTabLabel}
          </div>
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
};

const EmailCampaignModuleWithProviders: React.FC = () => (
  <CharacterLibraryProvider>
    <EmailCampaignModule />
  </CharacterLibraryProvider>
);

export default EmailCampaignModuleWithProviders;
